import { Component, EventEmitter, Input, Output, OnChanges, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { ButtonModule } from 'primeng/button';
import { ListboxModule } from 'primeng/listbox';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { Note } from '../../_service/data.service';

@Component({
  selector: 'app-note-link-dialog2',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    DialogModule,
    InputTextModule,
    ButtonModule,
    ListboxModule,
    ProgressSpinnerModule,
  ],
  template: `
    <p-dialog
      header="Link to Note"
      [(visible)]="visible"
      [modal]="true"
      [closable]="true"
      [draggable]="false"
      [resizable]="false"
      (onHide)="onDialogHide()"
      [style]="{ width: '600px', maxWidth: '90vw' }">

      <!-- Search Input -->
      <div class="mb-4">
        <label for="search-input" class="block text-sm font-medium mb-2">
          Search notes by title
        </label>
        <input
          type="text"
          pInputText
          id="search-input"
          [(ngModel)]="searchTerm"
          (ngModelChange)="onSearchTermChange($event)"
          placeholder="Type to search..."
          class="w-full"
          autofocus />
      </div>

      <!-- Loading Spinner -->
      <div class="flex justify-center py-8" *ngIf="loading">
        <p-progressSpinner styleClass="w-3rem h-3rem" />
      </div>

      <!-- Notes List -->
      <div *ngIf="!loading">
        <p-listbox
          *ngIf="filteredNotes.length > 0"
          class="note-link-listbox"
          [options]="filteredNotes"
          optionLabel="Title"
          optionValue="_id"
          [(ngModel)]="selectedNoteId"
          (onChange)="onNoteSelected($event)"
          [listStyle]="{ 'max-height': '300px' }">
        </p-listbox>

        <p *ngIf="filteredNotes.length === 0 && searchTerm.trim().length >= 2"
           class="text-sm text-gray-500 py-4 text-center">
          No notes found matching "{{ searchTerm }}"
        </p>

        <p *ngIf="searchTerm.trim().length < 2"
           class="text-sm text-gray-500 py-4 text-center">
          Type at least 2 characters to search
        </p>
      </div>

      <!-- Link Text Input -->
      <div class="mt-4" *ngIf="selectedNoteId">
        <label for="link-text" class="block text-sm font-medium mb-2">
          Link text
        </label>
        <input
          type="text"
          pInputText
          id="link-text"
          [(ngModel)]="linkText"
          placeholder="Text to display..."
          class="w-full" />
      </div>

      <!-- Footer Buttons -->
      <ng-template pTemplate="footer">
        <button
          pButton
          label="Cancel"
          class="p-button-text p-button-secondary"
          (click)="onCancel()">
        </button>
        <button
          pButton
          label="Insert Link"
          [disabled]="!selectedNoteId || !linkText.trim()"
          (click)="onConfirm()">
        </button>
      </ng-template>
    </p-dialog>
  `,
  styles: [
    `
      :host ::ng-deep .note-link-listbox .p-listbox-list {
        padding: 0.25rem;
      }

      :host ::ng-deep .note-link-listbox .p-listbox-item {
        padding: 0.5rem 0.75rem;
        margin: 0.125rem 0;
        border-radius: 4px;
        cursor: pointer;
        transition: background-color 150ms;
      }

      :host ::ng-deep .note-link-listbox .p-listbox-item:hover {
        background-color: rgba(59, 130, 246, 0.1);
      }

      :host ::ng-deep .note-link-listbox .p-highlight {
        background-color: rgba(59, 130, 246, 0.2) !important;
        color: inherit !important;
      }
    `,
  ],
})
export class NoteLinkDialog2Component implements OnChanges {
  @Input() visible = false;
  @Input() notes: Note[] = [];
  @Input() loading = false;
  @Input() initialLinkText = '';

  @Output() visibleChange = new EventEmitter<boolean>();
  @Output() confirm = new EventEmitter<{ noteId: string; linkText: string; noteTitle: string }>();
  @Output() cancel = new EventEmitter<void>();

  searchTerm = '';
  filteredNotes: Note[] = [];
  selectedNoteId: string | null = null;
  linkText = '';

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['notes']) {
      this.applyFilter();
    }
    if (changes['initialLinkText'] && this.initialLinkText) {
      this.linkText = this.initialLinkText;
    }
  }

  onSearchTermChange(value: string): void {
    this.searchTerm = value;
    this.applyFilter();
  }

  onNoteSelected(event: { value: string }): void {
    const selected = this.filteredNotes.find((n) => n._id === event.value);
    if (selected) {
      this.selectedNoteId = selected._id;
      // Auto-fill link text with note title if empty
      if (!this.linkText.trim()) {
        this.linkText = selected.Title;
      }
    }
  }

  onConfirm(): void {
    if (this.selectedNoteId && this.linkText.trim()) {
      const note = this.filteredNotes.find((n) => n._id === this.selectedNoteId);
      this.confirm.emit({
        noteId: this.selectedNoteId,
        linkText: this.linkText.trim(),
        noteTitle: note?.Title || '',
      });
      this.closeDialog();
    }
  }

  onCancel(): void {
    this.cancel.emit();
    this.closeDialog();
  }

  onDialogHide(): void {
    // Called when dialog is closed via X button or ESC
    this.closeDialog();
  }

  private closeDialog(): void {
    this.visible = false;
    this.visibleChange.emit(false);
    this.resetState();
  }

  private resetState(): void {
    this.searchTerm = '';
    this.filteredNotes = [];
    this.selectedNoteId = null;
    this.linkText = '';
  }

  private applyFilter(): void {
    const term = this.searchTerm.trim().toLowerCase();
    
    if (term.length < 2) {
      this.filteredNotes = [];
      return;
    }

    this.filteredNotes = this.notes.filter((note) =>
      note.Title.toLowerCase().includes(term)
    );
  }
}
